<!--
  A searchable block picker for the rules dialog (design skill §6 dialog
  policy): friendly type/name as the primary label, block ID as a secondary
  mono hint. Used for both the "Assessment answer" and "Component state"
  sources in RuleCard.vue.
-->
<script setup lang="ts">
import type { HTMLAttributes } from "vue";
import { cn } from "@/lib/utils";
import type { BlockSummary } from "./ruleModel";

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  modelValue?: string;
  blocks: BlockSummary[];
  placeholder?: string;
  emptyLabel?: string;
  class?: HTMLAttributes["class"];
}>();

const emit = defineEmits<{ "update:modelValue": [value: string] }>();

const open = ref(false);

const selected = computed(() => props.blocks.find((block) => block.id === props.modelValue));

function select(id: string) {
  emit("update:modelValue", id);
  open.value = false;
}
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <Button
        variant="outline"
        role="combobox"
        size="sm"
        :class="cn('h-8 w-full justify-between rounded-sm px-2.5 text-[13px] font-normal', props.class)"
      >
        <span class="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden text-left">
          <template v-if="selected">
            <span class="truncate">{{ selected.type || selected.componentId || "Block" }}</span>
            <span class="shrink-0 truncate font-mono text-[11px] text-muted-foreground">{{ selected.label }}</span>
          </template>
          <span v-else class="truncate text-muted-foreground">{{ placeholder ?? "Select a block" }}</span>
        </span>
        <Icon mode="svg" size="14" name="lucide:chevrons-up-down" class="ml-1 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-[320px] p-0" align="start">
      <Command>
        <CommandInput :placeholder="placeholder ?? 'Search blocks…'" />
        <CommandEmpty>{{ emptyLabel ?? "No blocks available." }}</CommandEmpty>
        <CommandList>
          <CommandGroup>
            <CommandItem
              v-for="block in blocks"
              :key="block.id"
              :value="`${block.type ?? ''} ${block.label} ${block.id}`"
              @select="select(block.id)"
            >
              <span class="flex min-w-0 flex-1 flex-col">
                <span class="truncate text-[13px]">{{ block.type || block.componentId || "Block" }} · {{ block.label }}</span>
                <span class="truncate font-mono text-[11px] text-muted-foreground">{{ block.id }}</span>
              </span>
              <Icon
                mode="svg"
                size="14"
                name="lucide:check"
                :class="cn('ml-auto shrink-0', block.id === modelValue ? 'opacity-100' : 'opacity-0')"
              />
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
